import { createContext, type RefObject, useContext, useEffect, useState } from 'react';

export interface ProjectHeaderInfo {
  id: string;
  name: string;
  module: string;
  collapsed: boolean;
  root: HTMLElement | null;
  modules: HTMLElement | null;
}
export interface InstallationHeaderInfo {
  name: string;
  pathname: string;
  collapsed: boolean;
  element: HTMLElement | null;
}
export const HeaderContext = createContext<{
  project: ProjectHeaderInfo | null;
  installation: InstallationHeaderInfo | null;
  height: number;
  setHeight: (height: number) => void;
  setProject: (project: ProjectHeaderInfo | null) => void;
  setInstallation: (installation: InstallationHeaderInfo | null) => void;
} | null>(null);

export function useHeaderContext() {
  return useContext(HeaderContext);
}

function usePassedHeader(ref: RefObject<HTMLElement | null>, enabled: boolean) {
  const height = useHeaderContext()?.height ?? 0;
  const [passed, setPassed] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) {
      setPassed(false);
      return;
    }
    const update = () => setPassed(element.getBoundingClientRect().bottom <= height);
    update();
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setPassed(entry.boundingClientRect.bottom <= height);
        },
        { rootMargin: `-${height}px 0px 0px 0px`, threshold: [0, 1] }
      );
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ref, enabled, height]);
  return passed;
}

export function useProjectHeader(
  id: string,
  name: string | undefined,
  module: string,
  root: RefObject<HTMLElement | null>,
  modules: RefObject<HTMLElement | null>
) {
  const context = useHeaderContext();
  const setProject = context?.setProject;
  const collapsed = usePassedHeader(modules, !!context && !!name);
  useEffect(() => {
    if (!setProject || !name) return;
    setProject({ id, name, module, collapsed, root: root.current, modules: modules.current });
    return () => setProject(null);
  }, [id, name, module, collapsed, root, modules, setProject]);
}

export function useInstallationHeader(
  name: string | undefined,
  pathname: string,
  ref: RefObject<HTMLElement | null>
) {
  const context = useHeaderContext();
  const setInstallation = context?.setInstallation;
  const collapsed = usePassedHeader(ref, !!context && !!name);
  useEffect(() => {
    if (!setInstallation || !name) return;
    setInstallation({ name, pathname, collapsed, element: ref.current });
    return () => setInstallation(null);
  }, [name, pathname, ref, collapsed, setInstallation]);
  return collapsed;
}

export function scrollToHeaderTarget(element: HTMLElement | null, headerHeight: number) {
  if (!element) return;
  window.scrollTo({
    top: Math.max(0, element.getBoundingClientRect().top + window.scrollY - headerHeight - 12),
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });
}
